import React, { ReactNode, useCallback, useState } from 'react'
import { MacroType, TemplateKeywordsField, Well } from '@pushly/aqe/lib/components'
import type { CustomTagProps } from '@rc-component/select/lib/BaseSelect'
import { GlobalOutlined, InfoCircleOutlined } from '@ant-design/icons'
import TemplateTitleField from './template-fields/title-field'
import TemplateBodyField from './template-fields/body-field'
import TemplateLandingUrlField from './template-fields/landing-url-field'
import LandingUrlActionButton from './template-fields/landing-url-action-button'
import { NotificationContentModel } from '../../../models/notification/notification-content.model'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import TemplateMediaField from './template-fields/media-field'
import { PromiseableProp } from '../../../_utils/promiseable-prop'
import { getClassNames } from '../../../_utils/classnames'
import { DomainDto } from '../../../dtos/domain'
import NotificationExperienceOptions from './notification-experience-options'
import CharacterLimitTooltip from './character-limit-tooltip'
import { NotificationBuilderLevel, NotificationBuilderTheme } from '../enums'
import { useService } from '../../../hooks/use-service'
import { AppState } from '../../../stores/app'
import { NotificationChannelSettings } from './notification-channel-settings'
import { NotificationNativeAndroidContentModel } from '../../../models/notification/notification-native-android-content.model'
import { NotificationNativeIosContentModel } from '../../../models/notification/notification-native-ios-content.model'
import { NotificationDefaultContentModel } from '../../../models/notification/notification-default-content.model'
import { NotificationWebContentModel } from '../../../models/notification/notification-web-content.model'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import { INotificationBuilderState } from '../interfaces/notification-builder-state.interface'
import { ActionDispatcher } from '../types'
import { MacroOptionType } from '@pushly/aqe/lib/components/macro-manager/macro-manager'
import { Divider, Flex, Popover, Skeleton, Switch, Tag, Typography } from 'antd'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import titleCase from 'title-case'
import { FieldSuggestionResultsManager } from './field-suggestion-results-manager/'
import { formatFieldNamesContent } from './field-suggestion-results-manager/format-field-names'
import { useSharedChannelFieldSetter } from '../hooks/use-shared-channel-field-setter'
import { useFieldSuggestionResultsManager } from '../hooks/use-field-suggestion-results-setter'
import { NotificationActionsBuilder } from './notification-actions-builder'
import { RemoteErrorMessageDisplay } from './field-suggestion-results-manager/components/remote-error-message-display'
import { FEAT_ENABLE_NOTIFICATION_DISPLAY_BEHAVIOR } from '../../../constants'

interface INotificationContentBuilder {
    className?: string
    builder: INotificationBuilderState
    level: NotificationBuilderLevel
    theme: NotificationBuilderTheme
    domain: DomainDto
    pageAnalysisDomain?: DomainDto
    loading?: boolean
    title?: string
    value: NotificationContentModel
    channels: DeliveryChannel[]
    dispatchChanges: ActionDispatcher
    onChange: (value: NotificationContentModel) => void
    /**
     * Clears all fields and resets content to default values.
     * @note Not currently implemented
     */
    onClear: () => void
    disabled?: boolean
    experienceDisabled?: boolean
    keywordOptions?: PromiseableProp<any>
    customMacroOptions?: PromiseableProp<any>
    secretsMacroOptions?: PromiseableProp<any>
    hideMacroToggle?: boolean
    macroCategories?: { urlFields?: MacroType[]; messageFields?: MacroType[] }
    imageMacroToggleLabel?: string
    imageMacroValue?: string
    imageMacroIcon?: ReactNode
    hideTtlSettings?: boolean
    hidePageAnalysisToggle?: boolean
    hidePushlyAiToggle?: boolean
}

type ContentUpdate =
    | NotificationDefaultContentModel
    | NotificationWebContentModel
    | NotificationNativeIosContentModel
    | NotificationNativeAndroidContentModel

// Duration for field suggestion success notifications (in seconds)
const FIELD_SUGGESTION_SUCCESS_DURATION = 8
const FIELD_SUGGESTION_SUCCESS_NOTIFICATION_KEY = 'notification_content_suggestion_success'

const KEYWORD_TRUNCATION_THRESHOLD = 45

const SkeletonWrapper = (props: { loading; children }) => (
    <Skeleton loading={props.loading} active={true} avatar={false} title={false}>
        {props.children}
    </Skeleton>
)

const NotificationContentBuilder = (props: INotificationContentBuilder) => {
    const {
        className,
        builder,
        dispatchChanges,
        level,
        domain,
        pageAnalysisDomain,
        loading,
        value,
        channels = [],
        onChange,
        hideMacroToggle,
        macroCategories,
        hideTtlSettings,
        hidePageAnalysisToggle = false,
        hidePushlyAiToggle = false,
        disabled,
        experienceDisabled,
    } = props

    const appState = useService(AppState)

    const [usingAltNativeUrl, setUsingAltNativeUrl] = useState<boolean>(!!value.getDefaultContent().getNativeAltUrl())
    const [localNativeUrl, setLocalNativeUrl] = useState<string | null | undefined>(
        value.getDefaultContent().getNativeAltUrl(),
    )

    const isOrgLevel = level === NotificationBuilderLevel.ORG
    const isDomainLevel = level === NotificationBuilderLevel.DOMAIN
    const isJourneyLevel = level === NotificationBuilderLevel.CAMPAIGN
    const levelClassName = isJourneyLevel ? 'journey' : isOrgLevel ? 'org' : 'domain'

    const landingUrlMacroTypes = macroCategories?.urlFields ?? [
        'domain',
        'device',
        'location',
        'notification',
        'secrets',
        'custom',
    ]

    const messageFieldMacroTypes = macroCategories?.messageFields ?? ['location', 'secrets', 'custom']

    const workingModel = value.getDefaultContent().clone()

    // TODO: consider reworking to handle per channel updates more field specifically
    const handleChange = (update: ContentUpdate, channel?: DeliveryChannel) => {
        const change = value.clone()

        switch (channel) {
            case DeliveryChannel.WEB:
                change.setWebContent(update as NotificationWebContentModel)
                break
            case DeliveryChannel.NATIVE_IOS:
                change.setNativeIosContent(update as NotificationNativeIosContentModel)
                break
            case DeliveryChannel.NATIVE_ANDROID:
                change.setNativeAndroidContent(update as NotificationNativeAndroidContentModel)
                break
            default:
                change.setDefaultContent(update as NotificationDefaultContentModel)
        }

        onChange?.(change)
    }

    const setSharedChannelField = useSharedChannelFieldSetter(value, channels, onChange)

    const nativeChannelSelected =
        channels.includes(DeliveryChannel.NATIVE_IOS) || channels.includes(DeliveryChannel.NATIVE_ANDROID)
    const showIOSFields =
        (isOrgLevel || isDomainLevel || isJourneyLevel) && channels.includes(DeliveryChannel.NATIVE_IOS)
    const showUseDifferentUrlForNative = channels.includes(DeliveryChannel.WEB) && nativeChannelSelected

    const validMacroOptionTypes: MacroOptionType[] = []
    if (domain.ecommConfig?.itemType) {
        validMacroOptionTypes.push(domain.ecommConfig?.itemType)
    }

    /**
     * Extract the landing URL according to the notification channels targeted.
     * Default should be used - if missing web is priority followed by native if available.
     */
    const getTargetedLandingUrl = (content: NotificationContentModel) => {
        const enabledChannels = builder.channels
        const defaultContent = content.getDefaultContent()

        if (defaultContent.getLandingUrl()) {
            return defaultContent.getLandingUrl()
        } else {
            if (enabledChannels.includes(DeliveryChannel.WEB)) {
                return content.getWebContent().getLandingUrl()
            } else {
                return defaultContent.getNativeAltUrl()
            }
        }
    }

    const {
        localFieldSuggestionResults,
        setLocalFieldSuggestionResults,
        applyFieldSuggestionResults,
        suggestedImageComposite,
        setSuggestedImageComposite,
    } = useFieldSuggestionResultsManager(value, channels, onChange)

    const keywordTagRender = useCallback((tagProps: CustomTagProps) => {
        const { label, value: tagValue, closable, onClose } = tagProps
        const labelStr = String(label ?? tagValue ?? '')

        if (labelStr.length > KEYWORD_TRUNCATION_THRESHOLD) {
            const truncated = labelStr.slice(0, KEYWORD_TRUNCATION_THRESHOLD) + '…'
            return (
                <Popover content={labelStr} overlayStyle={{ maxWidth: 400, wordBreak: 'break-all' }}>
                    <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                        {truncated}
                    </Tag>
                </Popover>
            )
        }

        return (
            <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                {labelStr}
            </Tag>
        )
    }, [])

    const getPageAnalysisResults = useCallback(
        async (pageAnalysisURL: string, mode: 'auto' | 'advanced' = 'auto') => {
            try {
                const { suggestions: pageAnalysisResults, error: analysisError } = await builder.pageAnalyzer.analyze(
                    pageAnalysisURL,
                )

                if (analysisError) {
                    if (mode === 'advanced') {
                        setLocalFieldSuggestionResults({
                            source: 'page_analyzer',
                            data: [],
                            error: analysisError,
                        })
                        return
                    }

                    simpleNotification('warning', <RemoteErrorMessageDisplay error={analysisError} />)
                    return
                }

                if (!pageAnalysisResults) {
                    simpleNotification('info', 'We were unable to find any suggestions for the provided Landing URL.')
                    return
                }

                const resultFieldNames = Object.entries(pageAnalysisResults)
                    .filter(([k, v]) => {
                        // Only include actual notification fields
                        const notificationFields = ['title', 'body', 'imageUrl', 'keywords']
                        return notificationFields.includes(k) && (Array.isArray(v) ? !!v.length : !!v)
                    })
                    .map(([k]) => titleCase(k).replace(/\surl$/i, ''))

                const resultFieldNamesContent = formatFieldNamesContent(resultFieldNames)

                if (mode === 'advanced') {
                    setLocalFieldSuggestionResults({ source: 'page_analyzer', data: [pageAnalysisResults] })
                    return
                }

                simpleNotification(
                    'success',
                    <span>
                        We've update the notification {resultFieldNamesContent} with suggestions from the provided
                        Landing URL.
                    </span>,
                    FIELD_SUGGESTION_SUCCESS_DURATION,
                    FIELD_SUGGESTION_SUCCESS_NOTIFICATION_KEY,
                )

                applyFieldSuggestionResults(pageAnalysisResults, { source: 'page_analyzer' })
            } catch (pageAnalysisError) {
                if (pageAnalysisError) {
                    console.warn(pageAnalysisError)
                }
            }
        },
        [builder.pageAnalyzer, applyFieldSuggestionResults, setLocalFieldSuggestionResults],
    )

    const getPushlyAiResults = useCallback(
        async (_sourceLandingUrl: string, mode: 'auto' | 'advanced' = 'auto') => {
            try {
                if (mode === 'advanced') {
                    const { allSuggestions: pushlyAiAdvancedResults, error: advancedModeAiError } =
                        await builder.pushlyAi.generateNotificationVariants(
                            builder.variants[builder.selectedVariantIdx],
                        )

                    if (advancedModeAiError) {
                        setLocalFieldSuggestionResults({
                            source: 'pushly_ai',
                            data: [],
                            error: advancedModeAiError,
                        })
                        return
                    }

                    if (!pushlyAiAdvancedResults || pushlyAiAdvancedResults.length === 0) {
                        simpleNotification(
                            'info',
                            'We were unable to generate any suggestions for the provided Landing URL.',
                        )
                        return
                    }

                    setLocalFieldSuggestionResults({ source: 'pushly_ai', data: pushlyAiAdvancedResults })
                    return
                }

                // Auto mode - use the original method that returns one variant
                const { suggestions: pushlyAiResults, error: aiError } =
                    await builder.pushlyAi.generateNotificationVariant(builder.variants[builder.selectedVariantIdx])

                if (aiError) {
                    simpleNotification('warning', <RemoteErrorMessageDisplay error={aiError} />)
                    return
                }

                if (!pushlyAiResults) {
                    simpleNotification(
                        'info',
                        'We were unable to generate any suggestions for the provided Landing URL.',
                    )
                    return
                }

                const resultFieldNames = Object.entries(pushlyAiResults)
                    .filter(([k, v]) => {
                        // Only include actual notification fields
                        const notificationFields = ['title', 'body', 'imageUrl', 'keywords']
                        return notificationFields.includes(k) && (Array.isArray(v) ? !!v.length : !!v)
                    })
                    .map(([k]) => titleCase(k).replace(/\surl$/i, ''))

                const resultFieldNamesContent = formatFieldNamesContent(resultFieldNames)

                simpleNotification(
                    'success',
                    <span>
                        We've update the notification {resultFieldNamesContent} with generated suggestions from the
                        provided Landing URL.
                    </span>,
                    FIELD_SUGGESTION_SUCCESS_DURATION,
                    FIELD_SUGGESTION_SUCCESS_NOTIFICATION_KEY,
                )

                applyFieldSuggestionResults(pushlyAiResults, { source: 'pushly_ai' })
            } catch (pageAnalysisError) {
                if (pageAnalysisError) {
                    console.warn(pageAnalysisError)
                }
            }
        },
        [
            builder.pushlyAi,
            applyFieldSuggestionResults,
            setLocalFieldSuggestionResults,
            builder.variants,
            builder.selectedVariantIdx,
        ],
    )

    return (
        <>
            <Well
                className={getClassNames('notification-interactions-builder', levelClassName, className)}
                title="Destination"
                mode="ghost"
                footerMode="hidden"
                actions={
                    loading ? undefined : (
                        <div className="content-builder-actions">
                            <div className="message-info-toggle">
                                <CharacterLimitTooltip className={getClassNames('notification-message-info-overlay')}>
                                    <span role="button" onClick={() => {}} style={{ cursor: 'pointer' }}>
                                        <span>Character Limits</span>
                                        <InfoCircleOutlined className="info-icon" />
                                    </span>
                                </CharacterLimitTooltip>
                            </div>
                        </div>
                    )
                }
            >
                <SkeletonWrapper loading={loading}>
                    <TemplateLandingUrlField
                        className={getClassNames(null, 'landing-url-field', {
                            'show-page-analysis-toggle': !hidePageAnalysisToggle,
                            'show-pushly-ai-toggle': !hidePushlyAiToggle,
                        })}
                        disabled={disabled}
                        value={getTargetedLandingUrl(value)}
                        onChange={(v) => {
                            workingModel.setLandingUrl(v)
                            // when alt native url is not set, the landing url is shared across channels
                            if (!usingAltNativeUrl) {
                                setSharedChannelField('LandingUrl', v)
                            } else {
                                // restrict change to default / web
                                handleChange(workingModel)
                            }
                        }}
                        macroTypes={landingUrlMacroTypes}
                        customMacroOptions={props.customMacroOptions}
                        secretsMacroOptions={props.secretsMacroOptions}
                        validMacroOptionTypes={validMacroOptionTypes}
                        formItemProps={{ layout: 'vertical' }}
                        addon={
                            <LandingUrlActionButton
                                domain={pageAnalysisDomain ?? domain}
                                value={getTargetedLandingUrl(value)}
                                disabled={disabled}
                                hidePushlyAiToggle={hidePushlyAiToggle}
                                onPushlyAiClick={getPushlyAiResults}
                                hidePageAnalysisToggle={hidePageAnalysisToggle}
                                onPageAnalysisClick={getPageAnalysisResults}
                                segmentsLoaded={
                                    builder.availableSegmentsLoaded &&
                                    !!builder.variants[builder.selectedVariantIdx]?.getAudience()?.getSegmentIds()
                                        ?.length
                                }
                            />
                        }
                    />

                    {showUseDifferentUrlForNative && (
                        <TemplateLandingUrlField
                            disabled={!usingAltNativeUrl || disabled}
                            value={workingModel?.getNativeAltUrl()}
                            onChange={(v) => {
                                if (v) {
                                    setLocalNativeUrl(v)
                                    workingModel.setNativeAltUrl(v)
                                    setSharedChannelField('LandingUrl', v, {
                                        updateChannels: [DeliveryChannel.NATIVE_ANDROID, DeliveryChannel.NATIVE_IOS],
                                    })
                                }
                            }}
                            label={
                                <span className="native-alt-url">
                                    <Switch
                                        size="small"
                                        checked={usingAltNativeUrl}
                                        aria-label="Use a Different URL for Native App"
                                        onChange={(v) => {
                                            setUsingAltNativeUrl(v)
                                            if (v) {
                                                workingModel?.setNativeAltUrl(
                                                    workingModel.getNativeAltUrl() ?? localNativeUrl,
                                                )
                                                setSharedChannelField(
                                                    'LandingUrl',
                                                    workingModel.getNativeAltUrl() ?? localNativeUrl,
                                                    {
                                                        updateChannels: [
                                                            DeliveryChannel.NATIVE_ANDROID,
                                                            DeliveryChannel.NATIVE_IOS,
                                                        ],
                                                    },
                                                )
                                            } else {
                                                // when removing alternate url set any native channels back to the shared landing url field value
                                                workingModel?.setNativeAltUrl(undefined)
                                                setSharedChannelField('LandingUrl', workingModel.getLandingUrl(), {
                                                    updateChannels: [
                                                        DeliveryChannel.NATIVE_ANDROID,
                                                        DeliveryChannel.NATIVE_IOS,
                                                    ],
                                                })
                                            }
                                        }}
                                    />
                                    <span>Use a Different URL for Native App</span>
                                </span>
                            }
                            placeholder="myapp://story/12345"
                            macroTypes={landingUrlMacroTypes}
                            customMacroOptions={props.customMacroOptions}
                            secretsMacroOptions={props.secretsMacroOptions}
                            validMacroOptionTypes={validMacroOptionTypes}
                            formItemProps={{ layout: 'vertical' }}
                        />
                    )}
                </SkeletonWrapper>
            </Well>

            <Divider size="small" />

            <Well
                className={getClassNames('notification-design-builder', levelClassName, className)}
                title="Message"
                variant="ghost"
                footerMode="hidden"
            >
                <SkeletonWrapper loading={loading}>
                    <TemplateTitleField
                        placeholder="Notification title"
                        disabled={disabled}
                        value={workingModel.getTitle()}
                        onChange={(v) => {
                            workingModel.setTitle(v)
                            setSharedChannelField('Title', v)
                        }}
                        macroTypes={messageFieldMacroTypes}
                        customMacroOptions={props.customMacroOptions}
                        secretsMacroOptions={props.secretsMacroOptions}
                        validMacroOptionTypes={validMacroOptionTypes}
                        formItemProps={{ layout: 'vertical' }}
                    />
                    <TemplateBodyField
                        placeholder="Notification body"
                        disabled={disabled}
                        value={workingModel.getBody()}
                        onChange={(v) => {
                            workingModel.setBody(v)
                            setSharedChannelField('Body', v || null)
                        }}
                        macroTypes={messageFieldMacroTypes}
                        customMacroOptions={props.customMacroOptions}
                        secretsMacroOptions={props.secretsMacroOptions}
                        validMacroOptionTypes={validMacroOptionTypes}
                        formItemProps={{ layout: 'vertical' }}
                    />

                    <Flex gap={16}>
                        <TemplateMediaField
                            mobileFriendly={props.theme === NotificationBuilderTheme.MOBILE}
                            disabled={disabled}
                            label={
                                <span>
                                    <span className="label-with-component-r">Image:</span>
                                    <Popover
                                        classNames={{
                                            root: getClassNames('popover'),
                                            content: getClassNames('notification-builder-popover', 'image-popover'),
                                        }}
                                        content={
                                            <>
                                                <p>
                                                    This image will be displayed underneath the title and body of the
                                                    notification on Windows and Android. Recommended minimum size is
                                                    360x180 with an aspect ratio of 2:1.
                                                </p>
                                            </>
                                        }
                                    >
                                        <InfoCircleOutlined className="info-icon" />
                                    </Popover>
                                </span>
                            }
                            className="image-upload"
                            domainId={domain.id}
                            defaultDomainMediaUrl={isOrgLevel ? undefined : domain?.defaultIconUrl}
                            accountId={isOrgLevel ? domain.accountId : undefined}
                            defaultCompositeValue={
                                suggestedImageComposite && {
                                    url: suggestedImageComposite.url,
                                    filename: suggestedImageComposite.filename,
                                    providerOptions: {
                                        originalMediaUrl: suggestedImageComposite.originalMediaUrl,
                                        localMediaSource: suggestedImageComposite.src,
                                        imageState: suggestedImageComposite.imageState,
                                    },
                                }
                            }
                            value={workingModel.getImageUrl()}
                            onChange={(v) => {
                                setSuggestedImageComposite(undefined)
                                setSharedChannelField('ImageUrl', v ?? null)
                            }}
                            cropRatio={2}
                            imageType="notification-image"
                            pjpg={true}
                            forceCrop={true}
                            size="large"
                            hideMacroToggle={hideMacroToggle}
                            macroTag={builder.macroProps?.imageMacroProps?.tag || 'item.image'}
                            macroTagLabel={builder.macroProps?.imageMacroProps?.label || 'Use item image'}
                        />

                        <TemplateMediaField
                            disabled={disabled || (isOrgLevel && workingModel.getIsUsingDomainDefaultIcon())}
                            label={
                                <span>
                                    {isOrgLevel && (
                                        <Switch
                                            size="small"
                                            checked={workingModel.getIsUsingDomainDefaultIcon()}
                                            disabled={disabled}
                                            aria-label="Use Domain Default Icon"
                                            onChange={(v) => {
                                                const update = workingModel.clone()

                                                update.setIsUsingDomainDefaultIcon(v)
                                                update.setIconUrl(null)

                                                handleChange(update)
                                            }}
                                            style={{ marginRight: 8 }}
                                        />
                                    )}
                                    <span className="label-with-component-r">Icon:</span>
                                    <Popover
                                        classNames={{
                                            root: getClassNames('popover'),
                                            content: getClassNames('notification-builder-popover', 'icon-popover'),
                                        }}
                                        content={
                                            <>
                                                <p>
                                                    This image will be displayed as a medium-size icon next to the title
                                                    and body of the notification on all platforms. Recommended size is
                                                    512x512 and an aspect ratio of 1:1.
                                                </p>

                                                {isOrgLevel && (
                                                    <p>
                                                        <strong>Domain Default:</strong> When enabled the notification
                                                        will use each Domain's configured default icon when sent.
                                                    </p>
                                                )}

                                                <p>
                                                    <strong>Note:</strong> Changing this icon has no effect for Safari
                                                    notifications. Instead, the subscriber will receive a notification
                                                    with the default icon that was set on your domain when they
                                                    subscribed.
                                                </p>
                                            </>
                                        }
                                    >
                                        <InfoCircleOutlined className="info-icon" />
                                    </Popover>
                                </span>
                            }
                            className="icon-upload"
                            domainId={domain.id}
                            accountId={isOrgLevel ? domain.accountId : undefined}
                            value={workingModel.getIconUrl()}
                            onChange={(v) => {
                                const result = setSharedChannelField('IconUrl', v ?? null, {
                                    waitToCompleteChange: true,
                                })
                                if (!isOrgLevel) {
                                    result.getDefaultContent().setIsUsingDomainDefaultIcon(false)
                                }
                                onChange?.(result)
                            }}
                            cropRatio={1}
                            cropDim={[512, 512]}
                            imageType="notification-icon"
                            forceCrop={true}
                            render={
                                isOrgLevel && workingModel.getIsUsingDomainDefaultIcon()
                                    ? () => (
                                          <button
                                              className="sw-file-uploader-btn ant-upload ant-upload-select ant-upload-select-picture-card image-mode icon-upload disabled"
                                              disabled={true}
                                          >
                                              <span className="ant-upload contents" role="button">
                                                  <div className="faux-btn">
                                                      <GlobalOutlined />
                                                      <span>Domain Icon</span>
                                                  </div>
                                              </span>
                                          </button>
                                      )
                                    : undefined
                            }
                        />
                    </Flex>
                </SkeletonWrapper>
            </Well>

            <Divider size="small" />

            <Well
                className={getClassNames(
                    'notification-interactions-builder',
                    'buttons-builder thin',
                    levelClassName,
                    className,
                    {
                        collapsed: !workingModel.getCustomActionsEnabled(),
                    },
                )}
                title={
                    <>
                        <Switch
                            size="small"
                            disabled={disabled}
                            checked={workingModel.getCustomActionsEnabled()}
                            aria-label="Customize Buttons"
                            onChange={(enabled) => {
                                const update = workingModel.clone()

                                update.setCustomActionsEnabled(enabled)
                                if (!enabled) {
                                    update.setActions(undefined)
                                }

                                handleChange(update)
                            }}
                        />

                        <span className="title">
                            <span className="label-with-component-r">Customize Buttons</span>
                            <Popover
                                classNames={{
                                    root: getClassNames('popover'),
                                    content: getClassNames('notification-builder-popover', 'buttons-popover'),
                                }}
                                content={
                                    <div>
                                        <Typography.Paragraph>
                                            By default, all notifications have a “Visit Site” button that, when clicked,
                                            directs the user to the primary landing URL. Enabling this setting allows
                                            you to customize the button behavior, label, & landing URL and add an
                                            optional 2nd button.
                                        </Typography.Paragraph>
                                        <Typography.Paragraph>
                                            Learn more about{' '}
                                            <a
                                                href={`${appState.documentationLink}/platform/notifications/action-buttons`}
                                                target="_blank"
                                            >
                                                Custom Buttons
                                            </a>
                                            .
                                        </Typography.Paragraph>
                                    </div>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </span>
                    </>
                }
                variant="ghost"
                footerMode="hidden"
                collapsed={!workingModel.getCustomActionsEnabled()}
            >
                <SkeletonWrapper loading={loading}>
                    {workingModel.getCustomActionsEnabled() && (
                        <NotificationActionsBuilder
                            loading={loading}
                            domain={isOrgLevel ? null : domain}
                            customMacroOptions={props.customMacroOptions}
                            secretsMacroOptions={props.secretsMacroOptions}
                            validMacroOptionTypes={validMacroOptionTypes}
                            value={workingModel}
                            onChange={(v) => handleChange?.(v)}
                        />
                    )}
                </SkeletonWrapper>
            </Well>

            <Divider size="small" />

            <Well
                className={getClassNames('notification-tracking-builder', levelClassName, className)}
                title="Tracking"
                mode="ghost"
                hideFooter={true}
            >
                <SkeletonWrapper loading={loading}>
                    <TemplateKeywordsField
                        disabled={disabled}
                        value={workingModel.getKeywords()}
                        onChange={(v) => {
                            workingModel.setKeywords(v)
                            handleChange(workingModel)
                        }}
                        options={props.keywordOptions}
                        max={30}
                        tagRender={keywordTagRender}
                        label={
                            <span className="keywords-label">
                                <span className="label-with-component-r">Keywords</span>
                                <Popover
                                    classNames={{
                                        root: getClassNames('popover'),
                                        content: getClassNames('notification-builder-popover', 'keywords-popover'),
                                    }}
                                    content={
                                        <div>
                                            <Typography.Paragraph>
                                                Every user who <strong>clicks</strong> on this notification will be
                                                automatically tagged with the provided keywords. You can then build user
                                                segments based on users tagged with specific keywords.
                                            </Typography.Paragraph>
                                            <Typography.Paragraph>
                                                You can add multiple keywords by pressing the enter/return key or
                                                entering a comma after each individual keyword.
                                            </Typography.Paragraph>
                                        </div>
                                    }
                                >
                                    <InfoCircleOutlined className="info-icon" />
                                </Popover>
                            </span>
                        }
                        placeholder={'Notification keywords'}
                        emptyOptionsPlaceholder={'No previously used keywords'}
                        formItemProps={{ layout: 'vertical' }}
                    />
                </SkeletonWrapper>
            </Well>

            <Divider size="small" />

            <NotificationExperienceOptions
                key="variant-experience-options"
                loading={loading}
                title="Behavior"
                domain={domain}
                disabled={disabled || experienceDisabled}
                hideTtlSettings={hideTtlSettings}
                hideCollapseKeySettings={
                    builder.type === 'template' || !domain.flags?.includes(FEAT_ENABLE_NOTIFICATION_DISPLAY_BEHAVIOR)
                }
                value={workingModel.getExperienceOptions()}
                onChange={(v) => {
                    const update = workingModel.clone()
                    update.setExperienceOptions(v)
                    handleChange(update)
                }}
            />

            {nativeChannelSelected && (
                <>
                    <Divider size="small" />

                    <NotificationChannelSettings
                        key="notification-channel-settings"
                        builder={builder}
                        loading={loading}
                        title="Channel Settings"
                        domain={domain}
                        disabled={disabled}
                        iosValue={value.clone()?.getNativeIosContent()}
                        androidValue={value.clone()?.getNativeAndroidContent()}
                        dispatchChanges={props.dispatchChanges}
                        onChange={(v) => {
                            if (v instanceof NotificationNativeAndroidContentModel) {
                                handleChange(v, DeliveryChannel.NATIVE_ANDROID)
                            }

                            if (v instanceof NotificationNativeIosContentModel) {
                                handleChange(v, DeliveryChannel.NATIVE_IOS)
                            }
                        }}
                        hideNativeIos={!channels.includes(DeliveryChannel.NATIVE_IOS)}
                        hideNativeAndroid={!channels.includes(DeliveryChannel.NATIVE_ANDROID)}
                        hideEnableToggle={true}
                    />
                </>
            )}

            <FieldSuggestionResultsManager
                domain={domain}
                results={localFieldSuggestionResults}
                builder={builder}
                dispatch={dispatchChanges}
                onAccept={async (
                    accepted,
                    addtVariants,
                    replaceTargetIndex,
                    additionalReplacements,
                    usedVariantIndices,
                ) => {
                    // Use the source from the results that were passed to the drawer
                    // so page_analyzer results are correctly attributed
                    const resultsSource = localFieldSuggestionResults?.source ?? 'page_analyzer'

                    // Synchronize auto-fill with advanced drawer selections
                    if (usedVariantIndices?.length) {
                        builder.pushlyAi.markVariantsUsed(usedVariantIndices)
                    }

                    if (accepted) {
                        if (replaceTargetIndex !== undefined && replaceTargetIndex !== builder.selectedVariantIdx) {
                            // Target differs from currently selected variant — apply directly to target
                            const targetVariant = builder.variants[replaceTargetIndex]
                            if (targetVariant) {
                                const update = targetVariant.clone()
                                const contentModel = update.getContent()
                                const defaultContent = contentModel.getDefaultContent().clone()

                                // Apply AI selections to the target's content
                                if (accepted.title) {
                                    defaultContent.setTitle(accepted.title)
                                }
                                if (accepted.body) {
                                    defaultContent.setBody(accepted.body)
                                }
                                if (accepted.imageUrl) {
                                    defaultContent.setImageUrl(accepted.imageUrl)
                                }
                                if (accepted.keywords) {
                                    const existingKw = defaultContent.getKeywords() ?? []
                                    const compiledKws = Array.from(new Set([...existingKw, ...accepted.keywords]))
                                    defaultContent.setKeywords(compiledKws)
                                }

                                // Persist AI generation metadata
                                const tone = accepted.tone ?? accepted.sourceMetadata?.tone
                                const reason = accepted.reason ?? accepted.sourceMetadata?.reason
                                defaultContent.setAutoSuggestedFieldsResults({
                                    source: resultsSource,
                                    sourceMetadata:
                                        tone || reason
                                            ? { tone: tone ?? undefined, reason: reason ?? undefined }
                                            : null,
                                    title: accepted.title ?? null,
                                    body: accepted.body ?? null,
                                    imageUrl: accepted.imageUrl ?? null,
                                    compositeImage: accepted.compositeImage ?? null,
                                    keywords: accepted.keywords ?? null,
                                })

                                contentModel.setDefaultContent(defaultContent)
                                update.setContent(contentModel)

                                dispatchChanges({
                                    entity: 'variant',
                                    type: 'patch',
                                    data: { id: replaceTargetIndex, variant: update },
                                })

                                setLocalFieldSuggestionResults(null)
                            }
                        } else {
                            // Target is the currently selected variant — use existing path
                            applyFieldSuggestionResults(accepted, { source: resultsSource })
                        }
                    }

                    // Process additional replacements (multi-replace support)
                    if (additionalReplacements?.length) {
                        for (const replacement of additionalReplacements) {
                            const targetVariant = builder.variants[replacement.targetIndex]
                            if (!targetVariant) continue

                            if (replacement.targetIndex === builder.selectedVariantIdx) {
                                // Target is the currently selected variant
                                applyFieldSuggestionResults(replacement.selections, { source: resultsSource })
                            } else {
                                const update = targetVariant.clone()
                                const contentModel = update.getContent()
                                const defaultContent = contentModel.getDefaultContent().clone()

                                if (replacement.selections.title) {
                                    defaultContent.setTitle(replacement.selections.title)
                                }
                                if (replacement.selections.body) {
                                    defaultContent.setBody(replacement.selections.body)
                                }
                                if (replacement.selections.imageUrl) {
                                    defaultContent.setImageUrl(replacement.selections.imageUrl)
                                }
                                if (replacement.selections.keywords) {
                                    const existingKw = defaultContent.getKeywords() ?? []
                                    const compiledKws = Array.from(
                                        new Set([...existingKw, ...replacement.selections.keywords]),
                                    )
                                    defaultContent.setKeywords(compiledKws)
                                }

                                const tone = replacement.selections.tone ?? replacement.selections.sourceMetadata?.tone
                                const reason =
                                    replacement.selections.reason ?? replacement.selections.sourceMetadata?.reason
                                defaultContent.setAutoSuggestedFieldsResults({
                                    source: resultsSource,
                                    sourceMetadata:
                                        tone || reason
                                            ? { tone: tone ?? undefined, reason: reason ?? undefined }
                                            : null,
                                    title: replacement.selections.title ?? null,
                                    body: replacement.selections.body ?? null,
                                    imageUrl: replacement.selections.imageUrl ?? null,
                                    compositeImage: replacement.selections.compositeImage ?? null,
                                    keywords: replacement.selections.keywords ?? null,
                                })

                                contentModel.setDefaultContent(defaultContent)
                                update.setContent(contentModel)

                                dispatchChanges({
                                    entity: 'variant',
                                    type: 'patch',
                                    data: { id: replacement.targetIndex, variant: update },
                                })
                            }
                        }

                        setLocalFieldSuggestionResults(null)
                    }

                    if (addtVariants?.length) {
                        const currentVariant = builder.variants[builder.selectedVariantIdx]!

                        // Check if we can add all variants
                        const totalAfterAdd = builder.variants.length + addtVariants.length
                        if (totalAfterAdd > 4) {
                            simpleNotification(
                                'warning',
                                `Cannot add ${addtVariants.length} variants. Maximum of 4 variants allowed (currently have ${builder.variants.length}).`,
                            )
                            return
                        }

                        // Create all variants without validation or switching
                        const newVariants: NotificationVariantModel[] = []
                        for (const variant of addtVariants) {
                            const nextVariant = currentVariant.clone()

                            // Apply the AI-generated content to the new variant
                            const updatedContent = applyFieldSuggestionResults(variant, {
                                source: resultsSource,
                                skipOnChangeHandler: true,
                                replaceKeywords: true,
                            })
                            if (updatedContent) {
                                nextVariant.setContent(updatedContent)
                            }

                            newVariants.push(nextVariant)
                        }

                        // Add all variants at once using the new batch action
                        dispatchChanges({
                            entity: 'variants',
                            type: 'add-multi',
                            data: { variants: newVariants },
                        })

                        // Only show the success toast if we're already in A/B mode.
                        // When transitioning into A/B mode (1→N), the "A/B Testing enabled"
                        // notification in NotificationBuilder handles the feedback.
                        if (builder.variants.length > 1) {
                            simpleNotification('success', `${addtVariants.length} variants added successfully!`)
                        }

                        // Clear composite image state to prevent ghosting on the currently viewed variant
                        setSuggestedImageComposite(undefined)
                    }
                }}
                onClose={() => {
                    setLocalFieldSuggestionResults(null)
                }}
            />
        </>
    )
}

export default NotificationContentBuilder
